import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faCircle, faSearch, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { MessageErrorBoundary } from '../components/messaging';
import ConversationList from '../components/messaging/ConversationList';
import MessageThread from '../components/messaging/MessageThread';
import NewMessageModal from '../components/messaging/NewMessageModal';
import MessageSearchPanel from '../components/messaging/MessageSearchPanel';
import './MessagingPage.css';

function MessagingPage() {
  const [selectedThread, setSelectedThread] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  
  const { signOut, getDisplayName, isAuthenticated, getUserId, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // WebSocket connection
  const userId = getUserId();
  const { isConnected, error: wsError } = useWebSocket(userId);

  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
  };

  const handleNewMessage = () => {
    setShowNewMessage(true);
  };

  const handleCloseNewMessage = () => {
    setShowNewMessage(false);
  };

  const handleMessageSent = (newThread) => {
    setShowNewMessage(false);
    setSelectedThread(newThread);
  };

  const handleToggleSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleSearchSelectThread = (thread) => {
    setSelectedThread(thread);
    setShowSearch(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  // Show loading or redirect if not authenticated
  if (!isAuthenticated()) {
    return null; // ProtectedRoute will handle redirect
  }

  return (
    <Container fluid className="messaging-page">
      <div className="messaging-header">
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
          <div>
            <h4 className="mb-0">Messages</h4>
            <small className="text-muted">Welcome, {getDisplayName()}</small>
          </div>
          <div className="d-flex align-items-center gap-3">
            {/* Connection status indicator */}
            <div className="connection-status">
              <FontAwesomeIcon 
                icon={faCircle} 
                className={`connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}
              />
              <span className="connection-text">
                {isConnected ? 'Connected' : 'Connecting...'}
              </span>
            </div>
            
            {/* Admin button - only visible to administrators */}
            {isAdmin() && (
              <Button 
                variant="outline-warning"
                size="sm"
                onClick={handleGoToAdmin}
                title="Administration Panel"
              >
                <FontAwesomeIcon icon={faUserShield} className="me-2" />
                Admin
              </Button>
            )}
            
            {/* Search button */}
            <Button 
              variant={showSearch ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={handleToggleSearch}
            >
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              Search
            </Button>
            
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleSignOut}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
              Sign Out
            </Button>
          </div>
        </div>
        
        {/* WebSocket error alert */}
        {wsError && (
          <Alert variant="warning" className="mb-0 rounded-0">
            <small>
              Real-time messaging unavailable. Messages will be delivered when connection is restored.
            </small>
          </Alert>
        )}
      </div>
      
      <Row className="h-100">
        <Col md={4} lg={3} className="conversation-list-col">
          <MessageErrorBoundary 
            componentName="ConversationList"
            compact={true}
            onRetry={() => window.location.reload()}
          >
            {showSearch ? (
              <MessageSearchPanel
                onSelectThread={handleSearchSelectThread}
                onClose={handleToggleSearch}
              />
            ) : (
              <ConversationList
                selectedThreadId={selectedThread?.threadId}
                onSelectThread={handleSelectThread}
                onNewMessage={handleNewMessage}
              />
            )}
          </MessageErrorBoundary>
        </Col>
        <Col md={8} lg={9} className="message-thread-col">
          <MessageErrorBoundary 
            componentName="MessageThread"
            onRetry={() => setSelectedThread(null)}
          >
            {selectedThread ? (
              <MessageThread 
                thread={selectedThread} 
                currentUserId={userId}
              />
            ) : (
              <div className="no-conversation-selected">
                <div className="text-center text-muted">
                  <h4>Select a conversation to start messaging</h4>
                  <p>Choose a conversation from the list or start a new one</p>
                </div>
              </div>
            )}
          </MessageErrorBoundary>
        </Col>
      </Row>

      <NewMessageModal
        show={showNewMessage}
        onHide={handleCloseNewMessage}
        onMessageSent={handleMessageSent}
      />
    </Container>
  );
}

export default MessagingPage;
